/**
 * Card — canonical anatomy for AxiomGO chat-rendered cards (design-system §1,
 * cortex/design-system.md). Consolidates the 18 divergent card wrappers onto
 * ONE component + slots instead of a 4th pattern:
 *
 *   <Card tone="neutral|critical|warning" density="compact|comfortable">
 *     <Card.Header title="…" />
 *     <Card.Body text="…" />        (or <Card.Body>{'{table/form}'}</Card.Body> — never both)
 *     <Card.Actions><Button>…</Button></Card.Actions>
 *   </Card>
 *
 * Built ON TOP of the existing `TextureCard` primitive (nested-border texture,
 * already adopted by ProposalCard/FileCard/WebSearchResultCard/PreventivePlan*)
 * — not a 4th visual pattern. `TextureCard` stays exported/usable as-is for any
 * consumer not yet migrated onto this canonical anatomy.
 *
 * Structural invariants this component exists to force (design-system §1,
 * standard-core.md "Front"):
 *   - `Card.Body` renders exactly ONE text block (`text` prop) OR a
 *     table/form (`children`) — never both. Kills the header+deck+instruction
 *     stacking bug (F6) and the truncated+full description duplication (F4).
 *   - `Card.Header` never duplicates `Card.Body` — it is a short title only.
 *   - `Card.Actions` is a slot for `Button` (src/components/ui/button.tsx)
 *     only — `<button style=>` is forbidden here and in the gate grep.
 */
import * as React from 'react'
import { cva } from 'class-variance-authority'
import { cn } from '@/lib/utils'
import { TextureCard } from '@/components/ui/cult/texture-card'
import { Button } from '@/components/ui/button'

export type CardTone = 'neutral' | 'critical' | 'warning'
export type CardDensity = 'compact' | 'comfortable'

interface CardContextValue {
  tone: CardTone
  density: CardDensity
  /** Cartão substituído (histórico): só leitura, sem fileira de ações (QA99 item 6). */
  readOnly?: boolean
}

const CardContext = React.createContext<CardContextValue>({
  tone: 'neutral',
  density: 'comfortable',
})

function useCardContext(): CardContextValue {
  return React.useContext(CardContext)
}

/** tone → accent ring (independent of TextureCard's own nested border layers,
 *  so it never fights twMerge over the `border-*` utility group). */
const cardToneRing: Record<CardTone, string> = {
  neutral: '',
  critical: 'ring-1 ring-inset ring-axg-severity-crit/40',
  warning: 'ring-1 ring-inset ring-axg-severity-warn/40',
}

/** tone → small status dot shown by default in Card.Header (design-system:
 *  "ícone de tom"). Callers can override via the `icon` prop. */
const cardToneDot: Record<CardTone, string> = {
  neutral: 'bg-axg-accent',
  critical: 'bg-axg-severity-crit',
  warning: 'bg-axg-severity-warn',
}

/**
 * Responsive, not a hardcoded pixel value (design-system §6 / anti-pattern
 * fixed by the OnboardingCard responsive pass, #23) — `p-3 sm:p-4` / `p-4
 * sm:p-6` on the content surface, NEVER an inline `padding: '24px'`.
 */
const cardPaddingVariants = cva('', {
  variants: {
    density: {
      compact: 'p-3 sm:p-4',
      comfortable: 'p-4 sm:p-6',
    },
  },
  defaultVariants: { density: 'comfortable' },
})

/* ── Card (root) ─────────────────────────────────────────────────────── */

export interface CardProps extends React.HTMLAttributes<HTMLDivElement> {
  tone?: CardTone
  density?: CardDensity
  /**
   * QA99 item 6 — cartão substituído fica só leitura: `Card.Actions` não é
   * desenhado (botão com cara de ativo num cartão congelado é promessa falsa).
   */
  readOnly?: boolean
}

const CardRoot = React.forwardRef<HTMLDivElement, CardProps>(
  (
    { tone = 'neutral', density = 'comfortable', readOnly = false, className, children, ...props },
    ref,
  ) => (
    <CardContext.Provider value={{ tone, density, readOnly }}>
      <TextureCard
        ref={ref}
        data-testid="card"
        data-tone={tone}
        data-density={density}
        className={cn('mt-2', cardToneRing[tone], className)}
        {...props}
      >
        <div data-testid="card-surface" className={cn(cardPaddingVariants({ density }))}>
          {children}
        </div>
      </TextureCard>
    </CardContext.Provider>
  ),
)
CardRoot.displayName = 'Card'

/* ── Card.Header ─────────────────────────────────────────────────────── */

export interface CardHeaderProps extends Omit<React.HTMLAttributes<HTMLDivElement>, 'title'> {
  /** Short title (<=48 chars per design-system). NEVER the same text as Card.Body. */
  title: React.ReactNode
  /** Overrides the default tone dot. */
  icon?: React.ReactNode
  timestamp?: React.ReactNode
}

const CardHeader = React.forwardRef<HTMLDivElement, CardHeaderProps>(
  ({ title, icon, timestamp, className, children, ...props }, ref) => {
    const { tone } = useCardContext()
    return (
      <div
        ref={ref}
        data-testid="card-header"
        className={cn('mb-3 flex items-center justify-between gap-3', className)}
        {...props}
      >
        <div className="flex min-w-0 items-center gap-2">
          {icon ?? (
            <span
              aria-hidden="true"
              data-testid="card-header-tone-dot"
              className={cn('h-2 w-2 shrink-0 rounded-full', cardToneDot[tone])}
            />
          )}
          <h3
            data-testid="card-header-title"
            className="truncate text-[15px] font-semibold text-axg-primary"
          >
            {title}
          </h3>
        </div>
        {timestamp != null && (
          <span
            data-testid="card-header-timestamp"
            className="shrink-0 font-mono text-xs text-axg-muted"
          >
            {timestamp}
          </span>
        )}
        {children}
      </div>
    )
  },
)
CardHeader.displayName = 'Card.Header'

/* ── Card.Body ───────────────────────────────────────────────────────── */

export interface CardBodyProps extends React.HTMLAttributes<HTMLDivElement> {
  /**
   * The single text block (Axis' speech / a status line). Mutually exclusive
   * with `children` — a table/form goes in `children` instead. Passing both
   * is a design-system violation (§1: "NUNCA os dois empilhados") and logs a
   * dev-time error so it is caught before the gate grep.
   */
  text?: React.ReactNode
}

const CardBody = React.forwardRef<HTMLDivElement, CardBodyProps>(
  ({ text, children, className, ...props }, ref) => {
    if (import.meta.env.DEV && text != null && children != null) {
      console.error(
        '[Card.Body] recebeu `text` E `children` — o corpo do card só pode ter 1 bloco de ' +
          'texto OU 1 tabela/form, nunca os dois empilhados (cortex/design-system.md §1).',
      )
    }
    return (
      <div ref={ref} data-testid="card-body" className={cn('text-sm', className)} {...props}>
        {text != null ? (
          <p
            data-testid="card-body-text"
            className="m-0 font-mono text-[13px] leading-relaxed text-axg-secondary"
          >
            {text}
          </p>
        ) : (
          children
        )}
      </div>
    )
  },
)
CardBody.displayName = 'Card.Body'

/* ── Card.Actions ────────────────────────────────────────────────────── */

/** Canonical action footer. Usage and migration inventory: docs/development/card-actions.md. */
export type CardActionsProps = React.HTMLAttributes<HTMLDivElement>

const CardActions = React.forwardRef<HTMLDivElement, CardActionsProps>(
  ({ className, children, ...props }, ref) => {
    const { readOnly } = useCardContext()
    if (readOnly) return null
    return (
      <div
        ref={ref}
        data-testid="card-actions"
        data-card-actions
        className={cn(
          'mt-4 flex w-full min-w-0 flex-col flex-wrap items-stretch gap-2 border-t border-axg-border-subtle pt-3 sm:flex-row sm:items-center',
          '[&_button]:w-full [&_button]:max-w-full [&_button]:whitespace-normal sm:[&_button]:w-auto',
          '[&_a]:w-full [&_a]:max-w-full [&_a]:whitespace-normal sm:[&_a]:w-auto',
          className,
        )}
        {...props}
      >
        {children}
      </div>
    )
  },
)
CardActions.displayName = 'Card.Actions'

/* ── DetailExpander (part of Card.Body — in-place expand, design-system §5) ── */

export interface DetailExpanderProps {
  summary: React.ReactNode
  children: React.ReactNode
  defaultOpen?: boolean
  testId?: string
}

function DetailExpander({
  summary,
  children,
  defaultOpen = false,
  testId = 'detail-expander',
}: DetailExpanderProps) {
  const [open, setOpen] = React.useState(defaultOpen)
  return (
    <div data-testid={testId}>
      <Button
        type="button"
        variant="link"
        size="sm"
        data-testid={`${testId}-toggle`}
        aria-expanded={open}
        onClick={() => setOpen((o) => !o)}
        className="h-auto gap-1 p-0 font-mono text-xs text-axg-accent"
      >
        {summary}
      </Button>
      {open && (
        <div data-testid={`${testId}-content`} className="mt-2">
          {children}
        </div>
      )}
    </div>
  )
}
DetailExpander.displayName = 'DetailExpander'

/* ── Compose ─────────────────────────────────────────────────────────── */

type CardComponent = typeof CardRoot & {
  Header: typeof CardHeader
  Body: typeof CardBody
  Actions: typeof CardActions
  DetailExpander: typeof DetailExpander
}

const Card = CardRoot as CardComponent
Card.Header = CardHeader
Card.Body = CardBody
Card.Actions = CardActions
Card.DetailExpander = DetailExpander

export { Card }
export default Card
